import { useEffect } from 'react';
import company from '../data/company.json';

const ORIGIN = 'https://gp-cap.com';
const DEFAULT_IMAGE = `${ORIGIN}/gp-capital-company-og.png`;

const upsertMeta = (attrKey, attrValue, content) => {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attrKey}="${attrValue}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attrKey, attrValue);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const upsertLink = (rel, href) => {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

/**
 * <PageSEO title="..." description="..." path="/about" />
 *
 * Imperatively updates <title>, meta description, canonical, OG, and Twitter tags
 * on mount. Avoids react-helmet-async's strict single-string-child rule for <title>.
 *
 * Phase 74 — `referrerPolicy` allows pages to opt into a stricter referrer
 * policy than the site default. Used on sensitive legal/security/diligence
 * surfaces so outbound clicks (e.g. clicking the security.txt link from a
 * procurement portal) don't leak the full referrer URL. Reverted on unmount
 * so the rest of the SPA isn't affected.
 */
export const PageSEO = ({
  title,
  description,
  path = '/',
  image,
  noindex = false,
  type = 'website',
  referrerPolicy,
}) => {
  useEffect(() => {
    const safeTitle = typeof title === 'string' && title ? title : 'GP Capital';
    const fullTitle = safeTitle.includes('GP Capital') ? safeTitle : `${safeTitle} | GP Capital`;
    const safeDesc = typeof description === 'string' && description
      ? description
      : company.description;
    const url = `${ORIGIN}${path || '/'}`;
    const fullImage = image
      ? (image.startsWith('http') ? image : `${ORIGIN}${image}`)
      : DEFAULT_IMAGE;

    document.title = fullTitle;
    // Static metadata belongs to its original document, not the next SPA route.
    document.head.querySelectorAll('script[data-page-schema="true"]').forEach(node => {
      if (node.dataset.schemaPath !== path) node.remove();
    });
    if (!noindex && !document.head.querySelector('script[data-page-schema="true"]')) {
      const node = document.createElement('script');
      node.type = 'application/ld+json';
      node.dataset.pageSchema = 'true';
      node.dataset.schemaPath = path;
      node.textContent = JSON.stringify({ '@context':'https://schema.org', '@type':'WebPage', name:fullTitle, description:safeDesc, url, isPartOf:{'@id':ORIGIN+'#website'} });
      document.head.appendChild(node);
    }


    upsertMeta('name', 'description', safeDesc);
    upsertLink('canonical', url);

    if (noindex) {
      upsertMeta('name', 'robots', 'noindex, nofollow');
    } else {
      const robots = document.head.querySelector('meta[name="robots"]');
      if (robots) robots.remove();
    }

    upsertMeta('property', 'og:title', fullTitle);
    upsertMeta('property', 'og:description', safeDesc);
    upsertMeta('property', 'og:type', type);
    upsertMeta('property', 'og:url', url);
    upsertMeta('property', 'og:image', fullImage);
    upsertMeta('property', 'og:site_name', 'GP Capital');

    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', fullTitle);
    upsertMeta('name', 'twitter:description', safeDesc);
    upsertMeta('name', 'twitter:image', fullImage);

    // Phase 74 — opt-in stricter referrer policy on sensitive surfaces.
    // Revert on unmount so SPA-internal navigation away from a legal page
    // doesn't continue to suppress referrers on regular pages.
    if (referrerPolicy) {
      upsertMeta('name', 'referrer', referrerPolicy);
      return () => {
        const el = document.head.querySelector('meta[name="referrer"]');
        if (el) el.remove();
      };
    }
    return undefined;
  }, [title, description, path, image, noindex, type, referrerPolicy]);

  return null;
};

export default PageSEO;
